import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Download, 
  Share2, 
  Instagram, 
  Twitter,
  Copy,
  Check,
  Sparkles,
  MapPin,
  Calendar,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

export default function TimelineGenerator({ plan, isOpen, onClose }) {
  const timelineRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [style, setStyle] = useState('gradient'); // gradient, minimal, neon
  
  if (!plan || !isOpen) return null;
  
  const handleDownload = async () => {
    if (!timelineRef.current) return;
    
    try {
      setIsGenerating(true);
      const canvas = await html2canvas(timelineRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });
      
      const link = document.createElement('a');
      link.download = `panchi-trip-${plan.destination.toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Timeline downloaded! 📸');
    } catch (error) {
      toast.error('Failed to generate image');
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleCopyLink = () => {
    const shareText = `Check out my ${plan.duration_days}-day trip to ${plan.destination}! 🗺️✨ Planned with @PanchiAI`;
    navigator.clipboard.writeText(shareText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard!');
  };
  
  const handleShare = async (platform) => {
    const shareText = `Check out my ${plan.duration_days}-day trip to ${plan.destination}! 🗺️✨`;
    const url = window.location.href;
    
    if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({ title: `Trip to ${plan.destination}`, text: shareText, url });
      } catch (e) {
        console.log('Share cancelled');
      }
    }
  };
  
  const styleConfig = {
    gradient: {
      bg: 'bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f0f23]',
      accent: 'from-cyan-500 to-blue-600',
      text: 'text-white',
    },
    minimal: {
      bg: 'bg-[#0a0a0a]',
      accent: 'from-white to-gray-300',
      text: 'text-white',
    },
    neon: {
      bg: 'bg-black',
      accent: 'from-purple-500 to-pink-500',
      text: 'text-white',
    },
  };
  
  const config = styleConfig[style];
  const budget = plan.budget_breakdown || {};
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="w-full max-w-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header Controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={20} className="text-cyan-400" />
              <h3 className="text-lg font-semibold text-white">Timeline Generator</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Style Selector */}
          <div className="flex gap-2 mb-4">
            {['gradient', 'minimal', 'neon'].map((s) => (
              <button
                key={s}
                onClick={() => setStyle(s)}
                className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-all ${
                  style === s
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                    : 'bg-white/5 text-white/60 border border-white/10 hover:border-white/20'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          
          {/* Timeline Preview */}
          <div 
            ref={timelineRef}
            className={`${config.bg} rounded-3xl p-8 relative overflow-hidden`}
            style={{ minHeight: '500px' }}
          >
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-cyan-500/20 to-transparent rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-purple-500/20 to-transparent rounded-full blur-3xl" />
            
            {/* Content */}
            <div className="relative z-10">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/30 to-blue-500/30 flex items-center justify-center">
                  <span className="text-lg">🐦</span>
                </div>
                <span className="text-white/60 text-sm font-medium">PANCHI AI</span>
              </div>
              
              {/* Destination */}
              <h2 className={`text-4xl md:text-5xl font-bold ${config.text} tracking-tight mb-2`}>
                {plan.destination}
              </h2>
              
              <div className="flex items-center gap-4 text-white/60 mb-8">
                <span className="flex items-center gap-1">
                  <Calendar size={16} />
                  {plan.duration_days} Days
                </span>
                <span className="flex items-center gap-1">
                  <MapPin size={16} />
                  {plan.origin || 'India'}
                </span>
              </div>
              
              {/* Mini Timeline */}
              <div className="space-y-4 mb-8">
                {(plan.itinerary || []).slice(0, 4).map((day, idx) => (
                  <div key={day.day} className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.accent} flex items-center justify-center text-white font-bold text-sm`}>
                      {day.day}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-white">{day.title}</p>
                      {day.activities && day.activities[0] && (
                        <p className="text-white/50 text-sm truncate">{day.activities[0].title}</p>
                      )}
                    </div>
                  </div>
                ))}
                {(plan.itinerary || []).length > 4 && (
                  <p className="text-white/40 text-sm pl-14">+ {plan.itinerary.length - 4} more days...</p>
                )}
              </div>
              
              {/* Budget */}
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 backdrop-blur">
                <div>
                  <p className="text-white/50 text-sm">Estimated Budget</p>
                  <p className={`text-2xl font-bold bg-gradient-to-r ${config.accent} bg-clip-text text-transparent`}>
                    ₹{(budget.total || 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
              
              {/* Footer */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
                <p className="text-white/40 text-sm">Generated with AI ✨</p>
                <p className="text-white/40 text-sm">panchi.ai</p>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              onClick={handleDownload}
              disabled={isGenerating}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:scale-[1.02] transition-transform disabled:opacity-50"
            >
              <Download size={18} />
              {isGenerating ? 'Generating...' : 'Download Image'}
            </button>
            
            <button
              onClick={handleCopyLink}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 transition-colors"
            >
              {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
            </button>
            
            <button
              onClick={() => handleShare('twitter')}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 transition-colors"
            >
              <Twitter size={18} />
            </button>
            
            {navigator.share && (
              <button
                onClick={() => handleShare('native')}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 transition-colors"
              >
                <Share2 size={18} />
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
